import { createFileRoute, Outlet } from '@tanstack/react-router';
import { createAuthContextProvider, UnauthenticatedError } from '../utils/auth';
import { Box, Text } from '@streamelements/frontend-ui';
import { UnknownError } from '../components/UnknownError';
import {
  ensureOBSMinVersion,
  minVersionForMultiStreaming,
} from '../utils/ensureOBSPresence';
import { UnsupportedOBSVersion } from '../components/UnsupportedOBSVersion';
import { setTag } from '@sentry/react';
import { FatalError, UnsupportedOBSVersionError } from '../types/errors';
import { AnonymousAuthContextProvider, AstroConnection, AstroRealtimeEventEmitterProvider, PackageUpdateAvailabilityRealtimeClient, SimpleBoolValueProvider, SimpleJitterValueProvider } from '@streamelements/client-sdk-js';
import { DestinationsStorageApiClient } from '../services/DestinationsApiClient';
import { promisefy } from '../utils/promisefy';

async function setupPackageUpdateAvailabilityRealtimeClient(params: { sharedAstroConnection: AstroConnection }) {
  const packageId = 'obs-multistreaming';
  const jitterMinutes = 15;

  const broadcastChannel = new BroadcastChannel('PackageUpdateAvailabilityRealtimeClient:redresh');

  broadcastChannel.addEventListener('message', () => {
    //
    // We use a broadcast channel to synchronize reload between
    // several components of the system.
    //
    // A message is posted to this channel by packageUpdateAvailable event
    // handler below, so the first browser which reloads will trigger a
    // reload on all the browsers which are subscribed to this channel.
    //

    console.info(`Package update is available for package '${packageId}', reloading page...`);

    location.reload();
  });

  const canReloadAppBoolValueProvider = new SimpleBoolValueProvider({ value: false });

  const updateStreamingStatus = async () => {
    const currentStreamingStatus = await promisefy(window.host.getStreamingStatus)();

    // We only want to reload if the creator is not streaming at the moment
    canReloadAppBoolValueProvider.value = !currentStreamingStatus.isStreamingActive;
  };

  window.addEventListener('hostStreamingStarted', updateStreamingStatus);
  window.addEventListener('hostStreamingStopped', updateStreamingStatus);

  await updateStreamingStatus();

  const packageUpdateAvailabilityRealtimeClient = new PackageUpdateAvailabilityRealtimeClient({
    realtimeEventEmitterProvider: new AstroRealtimeEventEmitterProvider({
      sharedAstroConnection: params.sharedAstroConnection,
      authContextProvider: new AnonymousAuthContextProvider(),
      topicScope: 'none',
    }),
    packageIds: [packageId],
    dependencyStatusBoolValueProvider: canReloadAppBoolValueProvider, // We don't want updates to be sent while streaming is active
    jitterValueProvider: new SimpleJitterValueProvider({
      minimumValue: 0,
      maximumValue: jitterMinutes * 60 * 1000, // Up to jitterMinutes minutes
    }),
  });

  packageUpdateAvailabilityRealtimeClient.on('packageUpdateAvailable', () => {
    //
    // This will be called with jitter, so we don't overload the backend when reloading the page
    //
    // When our package updates, we want to refresh the page so a new version is loaded from the CDN.
    //

    console.info(`Package update is available for package '${packageId}', broadcasting reload and reloading page...`);

    // Notify other browsers that we want to reload
    broadcastChannel.postMessage('exec');

    // Reload our own browser, since BroadcastChannel does not notify our own
    // browser context about posted messages.
    location.reload();
  });

  // We are not disposing of packageUpdateAvailabilityRealtimeClient here, since it is supposed
  // to run and listen for package updates while the page is loaded at all times.

  window.__streamelements_packageUpdateAvailabilityRealtimeClient = packageUpdateAvailabilityRealtimeClient as never;
  window.__streamelements_packageUpdateAvailabilityBroadcastChannel = broadcastChannel as never;

  console.info(`Listening for package updates for package '${packageId}' with jitter of ${jitterMinutes} minutes`);
}

export const Route = createFileRoute('/_authenticated')({
  component: () => <Outlet />,
  beforeLoad: async ({ context: { serviceEnvironment } }) => {
    const { seLiveMultiStreamingMinVersionOk } = await ensureOBSMinVersion();

    if (!seLiveMultiStreamingMinVersionOk) {
      throw new UnsupportedOBSVersionError('OBS version is too old');
    }

    const authContextProvider = await createAuthContextProvider(serviceEnvironment);

    const { channel, isAnonymous } = await authContextProvider.getAuthContext();

    if (isAnonymous || !channel) {
      throw new UnauthenticatedError('User is not logged in');
    }

    window.__streamelements_channel = channel;

    setTag('channelId', channel._id);

    await new DestinationsStorageApiClient({ authContextProvider, serviceEnvironment }).initDestinations();

    const sharedAstroConnection = new AstroConnection({
        serviceEnvironment,
    });

    await setupPackageUpdateAvailabilityRealtimeClient({ sharedAstroConnection });

    return {
      authContextProvider,
      sharedAstroConnection,
    };
  },
  errorComponent: ({ error }) => {
    if (error instanceof UnauthenticatedError) {
      return <Unauthenticated />;
    }

    if (error instanceof UnsupportedOBSVersionError) {
      return <UnsupportedOBSVersion minVersion={minVersionForMultiStreaming} />;
    }

    return (<UnknownError>
      <Text.Body css={{
        color: error instanceof FatalError ? '$textError' : '$textAttention',
        mt: '1rem',
      }}>
        {error && error.message || error.toString()}
      </Text.Body>      
    </UnknownError>);
  },
});

function Unauthenticated() {
  return (
    <Box
      css={{
        size: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Text.Heading weight="bold" level="5">
        Unauthenticated
      </Text.Heading>
      <Text.Body css={{ color: '$textPrimary50' }}>You need to be logged in to use this feature.</Text.Body>
      <Text.Body css={{ color: '$textPrimary50' }}>
        If you did log in previously, please logout and login again.
      </Text.Body>
    </Box>
  );
}
